import { Step, Option } from '@de/types/types';
import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { SearchParamsOptions } from '@de/utils/types';
import { flattenStepsForDynamicOptions } from '@de/utils/flattenStepsForDynamicOptions';

/**
 * Gets the option(s) selected by the user for a step with a specific customField
 * @param customField - The custom field to search for
 * @param steps - Array of all steps in the flow
 * @param flowState - The flow state containing user selections
 * @returns The selected option(s) or undefined if not found
 */
export const getOptionByCustomField = (
  customField: string,
  steps: Step[],
  flowState: FlowState,
  searchParamsOptions?: SearchParamsOptions,
): Option | Option[] | undefined => {
  // Expand MULTI_QUESTION / DCS `stepQuestions` so nested owners resolve
  // the same way top-level QUESTION steps do (auto-population seeds under
  // the child id).
  const lookupSteps = flattenStepsForDynamicOptions(steps);

  const stepWithCustomField = lookupSteps.find(
    (step: Step) => step.customField === customField,
  );

  if (stepWithCustomField?.id) {
    const stepData = flowState[stepWithCustomField.id];
    if (stepData?.option) {
      return stepData.option as Option | Option[] | undefined;
    }
  }

  // Multi-`formKey` Configurable Steps (e.g. an entry step with several
  // dropdowns under one step id) have no step-level `customField` to
  // disambiguate a single dropdown — `customField` is authored per-option
  // instead (`_meta.customField` on the bound DSL node, see
  // `translateFormStateChange`/`buildOptionFromFormState`). Scan every
  // step's stored option(s) for a per-option match before falling back
  // to `searchParamsOptions`.
  for (const step of lookupSteps) {
    if (!step.id) continue;
    const stepData = flowState[step.id];
    const option = stepData?.option as Option | Option[] | undefined;
    if (!option) continue;

    if (Array.isArray(option)) {
      const match = option.find((o) => o.customField === customField);
      if (match) return match;
    } else if (option.customField === customField) {
      return option;
    }
  }

  return searchParamsOptions?.[customField];
};
